/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';

/**
 * An installation-statistics card whose "what does this count" note lives behind an
 * `i` button instead of standing permanently under the figure (the same pattern the
 * subscription overview cards use, 096). The prose is what an operator needs the first
 * time and clutter every time after, so the face of a card is figures only.
 *
 * A button rather than a `title` tooltip, because a hover tooltip is unreachable on a
 * phone and unreliable for a keyboard. It closes on a second click, on Escape and on a
 * click outside — the three ways anyone tries to dismiss a note. Pressing another card's
 * button is an outside click for this one, so only one note is ever open without any
 * shared state between the cards.
 *
 * The note floats out of flow, so opening it never changes the card's width or height
 * and the row never reflows under the operator. It hangs off the card's TITLE ROW —
 * which spans the card's content width — rather than off the button, so it lines up with
 * the card's own left and right edges instead of running off the right of the page.
 *
 * Props: { id, title, note, children }
 */
const InstallationInfoCard = ({ id, title, note, children }) => {
  const [open, setOpen] = useState(false);
  const card = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => { if (card.current && !card.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="au-card" data-testid={`inst-card-${id}`} ref={card}>
      <span className="au-card__label">
        {title}
        <button
          type="button"
          className="inst-info-btn"
          data-testid={`inst-info-btn-${id}`}
          aria-expanded={open}
          aria-label={`What ${title} counts`}
          onClick={() => setOpen((v) => !v)}
        >
          i
        </button>
        {open && (
          <span className="inst-info" role="note" data-testid={`inst-info-${id}`}>
            {note}
          </span>
        )}
      </span>
      {children}
    </div>
  );
};

export default InstallationInfoCard;
